import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import PaymentOptions from "@/components/PaymentOptions";
import { API, getConfig, inr } from "@/lib/api";
import { LogoFull } from "@/components/Logo";
import { OrderEmailNotice } from "@/components/OrderEmailNotice";
import { OrderPreparationNotice } from "@/components/OrderPreparationNotice";

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [waNumber, setWaNumber] = useState("");
  const [error, setError] = useState(false);

  const load = useCallback(() => axios.get(`${API}/orders/${orderId}`).then((r) => { setOrder(r.data); setError(false); }).catch(() => setError(true)), [orderId]);

  useEffect(() => {
    load();
    getConfig().then((c) => setWaNumber(c.whatsapp_number || ""));
  }, [load]);

  const waMessage = order
    ? `Hi Kandula Home Foods! I'd like to confirm my order ${order.order_number}:\n${order.items
        .map((i) => `• ${i.name}${i.unit ? ` (${i.unit})` : ""} x${i.quantity} — ${inr(i.line_total)}`)
        .join("\n")}\nTotal: ${inr(order.total)}\nName: ${order.customer.name}`
    : "";

  return (
    <div className="grain flex min-h-screen flex-col items-center bg-cream px-4 py-24 font-body" data-testid="order-success-page">
      <Link to="/" data-testid="success-home-link">
        <LogoFull />
      </Link>

      {error ? (
        <div className="mt-16 text-center" data-testid="order-error">
          <p className="font-display text-3xl text-ragi-dark" data-testid="order-access-title">This order is private</p>
          <p className="mt-3 max-w-sm text-sm text-muted-foreground" data-testid="order-access-message">Open it in the browser you used at checkout, or sign in to the account that placed it. For older guest orders, contact us with your order number.</p>
          <Link to="/account/orders" className="mt-4 block text-sm font-semibold text-terracotta underline" data-testid="order-signin-link">Sign in to view my orders</Link>
          <Link to="/" className="mt-4 inline-block text-sm font-semibold text-terracotta underline" data-testid="error-back-link">
            Back to the shop
          </Link>
        </div>
      ) : !order ? (
        <p className="mt-16 text-sm text-muted-foreground" data-testid="order-loading">Loading your order...</p>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 w-full max-w-lg rounded-3xl border border-cream-muted bg-cream-secondary p-8 shadow-xl shadow-ragi/5"
        >
          <div className="flex flex-col items-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ragi/10">
              <CheckCircle2 className="h-8 w-8 text-ragi" />
            </span>
            <h1 className="mt-4 font-display text-3xl font-bold text-ragi-dark" data-testid="order-confirmation-title">
              Order request received
            </h1>
            <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-terracotta" data-testid="order-number">
              {order.order_number}
            </p>
          </div>

          <div className="mt-8 space-y-3 border-t border-cream-muted pt-6" data-testid="order-items">
            {order.items.map((item) => (
              <div key={item.product_id} className="flex justify-between gap-3 text-sm" data-testid={`receipt-item-${item.product_id}`}>
                <span className="min-w-0 break-words text-ragi-dark">
                  {item.name} <span className="text-muted-foreground">{item.unit} x{item.quantity}</span>
                </span>
                <span className="shrink-0 font-semibold" data-testid={`receipt-line-total-${item.product_id}`}>{inr(item.line_total)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-cream-muted pt-3">
              <span className="font-semibold text-ragi-dark">Total</span>
              <span className="font-display text-xl font-bold text-terracotta" data-testid="order-total">
                {inr(order.total)}
              </span>
            </div>
          </div>

          <OrderPreparationNotice order={order} />
          <button onClick={load} className="mt-3 text-xs font-semibold text-ragi underline underline-offset-4" data-testid="order-refresh-status">Refresh order status</button>
          <PaymentOptions order={order} waNumber={waNumber} waMessage={waMessage} onUpdated={load} />
          <OrderEmailNotice status={order.email_status} />
          <Link to="/legal" className="mt-4 block text-center text-xs font-semibold text-ragi underline" data-testid="order-legal-link">Terms, preparation & refunds</Link>
          <Link to={`/order/${orderId}/tracking`} className="mt-5 block rounded-full border border-ragi/20 px-5 py-3 text-center text-sm font-semibold text-ragi transition-colors hover:bg-cream" data-testid="order-track-delivery-link">Track delivery</Link>

          <Link
            to="/"
            data-testid="continue-shopping-link"
            className="mt-4 block text-center text-sm font-semibold text-terracotta underline-offset-4 hover:underline"
          >
            Continue shopping
          </Link>
        </motion.div>
      )}
    </div>
  );
}
